import React, { useState, useEffect } from 'react';
import { libraryAPI } from '../../services/api';
import DataTable from '../../components/common/DataTable';
import SearchBar from '../../components/common/SearchBar';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import toast from 'react-hot-toast';
import { formatDate, formatCurrency } from '../../utils/helpers';

const LibraryManagement = () => {
  const [tab, setTab] = useState('books');
  const [books, setBooks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('book');
  const [form, setForm] = useState({});

  useEffect(() => {
    libraryAPI.categories().then(r => setCategories(r.data.results || r.data)).catch(() => {});
    loadBooks();
    loadIssues();
  }, []);

  useEffect(() => { loadBooks(); }, [page, search]);

  const loadBooks = () => {
    setLoading(true);
    libraryAPI.books({ page, page_size: 15, search }).then(r => {
      setBooks(r.data.results || r.data);
      setCount(r.data.count || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  const loadIssues = () => {
    libraryAPI.issues({ page_size: 20 }).then(r => setIssues(r.data.results || r.data)).catch(() => {});
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (modalType === 'book') await libraryAPI.createBook(form);
      else await libraryAPI.issueBook(form);
      toast.success(modalType === 'book' ? 'Book added!' : 'Book issued!');
      setShowModal(false);
      setForm({});
      loadBooks();
      loadIssues();
    } catch { toast.error('Failed to save'); }
  };

  const bookColumns = [
    { key: 'title', label: 'Title', render: (v, row) => <div><strong style={{ fontSize: 13 }}>{v}</strong><br /><small className="text-muted">by {row.author}</small></div> },
    { key: 'isbn', label: 'ISBN', render: v => v ? <code style={{ fontSize: 11 }}>{v}</code> : '—' },
    { key: 'category_name', label: 'Category' },
    { key: 'total_copies', label: 'Total' },
    { key: 'available_copies', label: 'Available', render: v => <span style={{ color: v > 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>{v}</span> },
    { key: 'rack_number', label: 'Rack', render: v => v || '—' },
    { key: 'price', label: 'Price', render: v => formatCurrency(v) },
  ];

  const issueColumns = [
    { key: 'book_title', label: 'Book', render: v => <strong>{v}</strong> },
    { key: 'member_name', label: 'Issued To' },
    { key: 'member_type', label: 'Type', render: v => <span className="badge bg-info text-dark">{v}</span> },
    { key: 'issue_date', label: 'Issue Date', render: v => formatDate(v) },
    { key: 'due_date', label: 'Due Date', render: v => formatDate(v) },
    { key: 'return_date', label: 'Return Date', render: v => v ? formatDate(v) : '—' },
    { key: 'fine_amount', label: 'Fine', render: v => v > 0 ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{formatCurrency(v)}</span> : '—' },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h2>Library Management</h2><p>Books, issues, returns, and fines</p></div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={() => { setModalType('issue'); setForm({}); setShowModal(true); }}>📖 Issue Book</button>
          <button className="btn btn-primary" onClick={() => { setModalType('book'); setForm({}); setShowModal(true); }}>➕ Add Book</button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {[
          { icon: '📚', label: 'Total Books', value: count, bg: '#eff6ff', color: '#1d4ed8' },
          { icon: '📖', label: 'Issued', value: issues.filter(i => i.status === 'issued').length, bg: '#fff7ed', color: '#c2410c' },
          { icon: '⚠️', label: 'Overdue', value: issues.filter(i => i.status === 'overdue').length, bg: '#fef2f2', color: '#b91c1c' },
          { icon: '📁', label: 'Categories', value: categories.length, bg: '#f0fdf4', color: '#15803d' },
        ].map(s => (
          <div key={s.label} className="col-6 col-lg-3">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: s.bg, color: s.color, fontSize: 22 }}>{s.icon}</div>
              <div className="stat-info"><h3>{s.value}</h3><p>{s.label}</p></div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex gap-2 mb-4">
        {[['books','📚 Books'],['issues','📖 Issues & Returns'],['overdue','⚠️ Overdue']].map(([k,l]) => (
          <button key={k} className={`btn ${tab === k ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'books' && (
        <div>
          <div className="card mb-3"><div className="card-body">
            <SearchBar onSearch={setSearch} placeholder="Search by title, author, ISBN..." />
          </div></div>
          <div className="card">
            <div className="card-header"><span>📚 Book Catalog ({count})</span></div>
            <DataTable columns={bookColumns} data={books} loading={loading} />
            <div className="card-body pt-0"><Pagination count={count} page={page} pageSize={15} onChange={setPage} /></div>
          </div>
        </div>
      )}

      {tab === 'issues' && (
        <div className="card">
          <div className="card-header"><span>📖 Book Issues & Returns</span></div>
          <DataTable columns={issueColumns} data={issues} loading={false} />
        </div>
      )}

      {tab === 'overdue' && (
        <div className="card">
          <div className="card-header"><span>⚠️ Overdue Books</span></div>
          <DataTable columns={issueColumns} data={issues.filter(i => i.status === 'overdue')} loading={false} />
        </div>
      )}

      <Modal show={showModal} onClose={() => setShowModal(false)} title={modalType === 'book' ? 'Add Book to Catalog' : 'Issue Book'} size="lg">
        <form onSubmit={handleSave}>
          {modalType === 'book' && (
            <div className="row">
              <div className="col-md-8 mb-3"><label className="form-label">Book Title *</label><input className="form-control" value={form.title || ''} onChange={e => setForm({...form, title: e.target.value})} required /></div>
              <div className="col-md-4 mb-3"><label className="form-label">ISBN</label><input className="form-control" value={form.isbn || ''} onChange={e => setForm({...form, isbn: e.target.value})} /></div>
              <div className="col-md-6 mb-3"><label className="form-label">Author *</label><input className="form-control" value={form.author || ''} onChange={e => setForm({...form, author: e.target.value})} required /></div>
              <div className="col-md-6 mb-3"><label className="form-label">Publisher</label><input className="form-control" value={form.publisher || ''} onChange={e => setForm({...form, publisher: e.target.value})} /></div>
              <div className="col-md-4 mb-3"><label className="form-label">Category</label>
                <select className="form-select" value={form.category || ''} onChange={e => setForm({...form, category: e.target.value})}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="col-md-4 mb-3"><label className="form-label">Total Copies</label><input type="number" className="form-control" value={form.total_copies || 1} onChange={e => setForm({...form, total_copies: e.target.value, available_copies: e.target.value})} /></div>
              <div className="col-md-4 mb-3"><label className="form-label">Price (₹)</label><input type="number" step="0.01" className="form-control" value={form.price || ''} onChange={e => setForm({...form, price: e.target.value})} /></div>
              <div className="col-md-4 mb-3"><label className="form-label">Rack Number</label><input className="form-control" value={form.rack_number || ''} onChange={e => setForm({...form, rack_number: e.target.value})} /></div>
            </div>
          )}
          {modalType === 'issue' && (
            <div>
              <div className="mb-3"><label className="form-label">Book *</label>
                <select className="form-select" value={form.book || ''} onChange={e => setForm({...form, book: e.target.value})} required>
                  <option value="">Select Book</option>
                  {books.filter(b => b.available_copies > 0).map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                </select>
              </div>
              <div className="mb-3"><label className="form-label">Member Type</label>
                <select className="form-select" value={form.member_type || 'student'} onChange={e => setForm({...form, member_type: e.target.value})}>
                  <option value="student">Student</option><option value="staff">Staff</option>
                </select>
              </div>
              <div className="mb-3"><label className="form-label">Issue Date *</label><input type="date" className="form-control" value={form.issue_date || new Date().toISOString().split('T')[0]} onChange={e => setForm({...form, issue_date: e.target.value})} required /></div>
              <div className="mb-3"><label className="form-label">Due Date *</label><input type="date" className="form-control" value={form.due_date || ''} onChange={e => setForm({...form, due_date: e.target.value})} required /></div>
            </div>
          )}
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">{modalType === 'book' ? '➕ Add Book' : '📖 Issue Book'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
export default LibraryManagement;
